import React, { useState, useEffect } from 'react';
import { Truck, Users, Route, MapPin, Package, BarChart, FileText, Settings, Plus, User, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import LogisticsRequests from './components/requests/LogisticsRequests';
import FleetManagement from './components/fleet/FleetManagement';
import RouteOptimization from './components/optimization/RouteOptimization';
import ExecutionTracking from './components/tracking/ExecutionTracking';
import LogisticsAnalytics from './components/analytics/LogisticsAnalytics';
import LogisticsReports from './components/reports/LogisticsReports';
import LogisticsSettings from './components/settings/LogisticsSettings';

const LogisticsDashboard = () => {
  const [activeTab, setActiveTab] = useState('requests');
  const [userRole, setUserRole] = useState('manager'); // 'user', 'driver', 'manager'

  // Role-based tabs configuration
  const getTabsForRole = (role) => {
    const allTabs = [
      { id: 'requests', label: 'Requests', icon: Package },
      { id: 'fleet', label: 'Fleet & Drivers', icon: Truck },
      { id: 'optimization', label: 'Route Optimization', icon: Route },
      { id: 'tracking', label: 'Live Tracking', icon: MapPin },
      { id: 'analytics', label: 'Analytics', icon: BarChart },
      { id: 'reports', label: 'Reports', icon: FileText },
      { id: 'settings', label: 'Settings', icon: Settings }
    ];

    switch (role) {
      case 'user':
        // Users can only see requests and tracking
        return allTabs.filter(tab => ['requests', 'tracking'].includes(tab.id));
      case 'driver':
        // Drivers see requests (their tasks), tracking, and reports
        return allTabs.filter(tab => ['requests', 'tracking', 'reports'].includes(tab.id));
      case 'manager':
      default:
        // Managers see everything
        return allTabs;
    }
  };

  const tabs = getTabsForRole(userRole);

  // Ensure activeTab is valid for current role
  useEffect(() => {
    if (!tabs.find(tab => tab.id === activeTab)) {
      setActiveTab(tabs[0]?.id || 'requests');
    }
  }, [userRole, tabs, activeTab]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold">Logistics Management System</h1>
            <p className="text-blue-100 mt-1">
              {userRole === 'user' && 'Submit and track your logistics requests'}
              {userRole === 'driver' && 'View your assigned tasks and update delivery status'}
              {userRole === 'manager' && 'Unified logistics requests, fleet management, route optimization, and real-time tracking'}
            </p>
          </div>
          <div className="flex gap-3">
            {/* Role Toggle */}
            <div className="bg-white/10 rounded-lg p-1 backdrop-blur-sm flex gap-1">
              <button
                onClick={() => setUserRole('user')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  userRole === 'user' ? 'bg-white text-blue-700' : 'text-white/90 hover:bg-white/10'
                }`}
                title="User View - Requestor"
              >
                <User className="w-4 h-4" />
                User
              </button>
              <button
                onClick={() => setUserRole('driver')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  userRole === 'driver' ? 'bg-white text-blue-700' : 'text-white/90 hover:bg-white/10'
                }`}
                title="Driver View - Execution"
              >
                <Truck className="w-4 h-4" />
                Driver
              </button>
              <button
                onClick={() => setUserRole('manager')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  userRole === 'manager' ? 'bg-white text-blue-700' : 'text-white/90 hover:bg-white/10'
                }`}
                title="Manager View - Full Access"
              >
                <Shield className="w-4 h-4" />
                Manager
              </button>
            </div>
            {(userRole === 'user' || userRole === 'manager') && (
              <button
                onClick={() => {
                  setActiveTab('requests');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="px-4 py-2 bg-white text-blue-700 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2 font-medium"
              >
                <Plus className="w-5 h-5" />
                New Request
              </button>
            )}
          </div>
        </div>

        {/* Centered Tabs */}
        <div className="mt-6 flex justify-center">
          <div className="inline-flex space-x-1 bg-white/10 rounded-lg p-1 backdrop-blur-sm">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white text-blue-700'
                      : 'text-white/90 hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'requests' && <LogisticsRequests userRole={userRole} />}
        {activeTab === 'fleet' && <FleetManagement userRole={userRole} />}
        {activeTab === 'optimization' && <RouteOptimization userRole={userRole} />}
        {activeTab === 'tracking' && <ExecutionTracking userRole={userRole} />}
        {activeTab === 'analytics' && <LogisticsAnalytics userRole={userRole} />}
        {activeTab === 'reports' && <LogisticsReports userRole={userRole} />}
        {activeTab === 'settings' && <LogisticsSettings userRole={userRole} />}
      </div>
    </div>
  );
};

export default LogisticsDashboard;
